import { CloseIcon, UploadIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Typography } from '@components/typography';
import { formatSizeFromBytes } from '@helper-functions/formatSize';
import { Box, Stack, alpha, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type FileUploadProps = {
  accept?: string;
  name?: string;
  label?: string;
  multiple?: boolean;
  value?: File | File[] | null;
  onFileLoad?: (file: File | File[] | null) => void;
  onRemove?: (index: number) => void;
  webkitdirectory?: boolean;
  error?: string | false;
};

export const FileUpload = ({
  accept,
  name,
  label,
  multiple = false,
  value,
  onFileLoad,
  onRemove,
  webkitdirectory,
  error,
}: FileUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalFiles, setInternalFiles] = useState<File[]>([]);
  const [dragCounter, setDragCounter] = useState(0);
  const theme = useTheme();

  // Determine if component is controlled or uncontrolled
  const isControlled = value !== undefined;
  const files = useMemo(() => {
    if (!isControlled) return internalFiles;
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  }, [isControlled, value, internalFiles]);

  // Sync internal state with controlled value
  useEffect(() => {
    if (isControlled) {
      const newFiles = Array.isArray(value) ? value : value ? [value] : [];
      setInternalFiles(newFiles);
    }
  }, [value, isControlled]);

  const handleFilesSelected = useCallback(
    (newFiles: File[]) => {
      if (multiple) {
        const updatedFiles = [...files, ...newFiles];
        if (!isControlled) {
          setInternalFiles(updatedFiles);
        }
        onFileLoad?.(updatedFiles);
      } else {
        const updatedFiles = [newFiles[0]];
        if (!isControlled) {
          setInternalFiles(updatedFiles);
        }
        onFileLoad?.(newFiles[0]);
      }
    },
    [files, multiple, onFileLoad, isControlled],
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      if (onRemove) {
        onRemove(index);
      } else {
        const updatedFiles = [...files.slice(0, index), ...files.slice(index + 1)];
        if (!isControlled) {
          setInternalFiles(updatedFiles);
        }
        if (multiple) {
          onFileLoad?.(updatedFiles);
        } else {
          onFileLoad?.(null);
        }
      }
    },
    [files, multiple, onFileLoad, onRemove, isControlled],
  );

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev - 1);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(0);

      const droppedFiles = Array.from(e.dataTransfer.files || []);
      if (droppedFiles.length > 0) {
        handleFilesSelected(droppedFiles);
      }
    },
    [handleFilesSelected],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.currentTarget.files || []);
      if (selectedFiles.length > 0) {
        handleFilesSelected(selectedFiles);
      }
    },
    [handleFilesSelected],
  );

  const isDragActive = dragCounter > 0;

  return (
    <>
      <Stack
        onClick={() => inputRef.current?.click()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          border: isDragActive ? '2px dashed' : '1px solid',
          borderColor: isDragActive
            ? theme.palette.fileUpload.border.drag
            : error
              ? theme.palette.error.main
              : theme.palette.fileUpload.border.default,
          borderRadius: theme.spacing(1.5),
          padding: theme.spacing(3),
          minHeight: '100px',
          maxHeight: files.length > 1 ? '300px' : '150px',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: isDragActive
            ? alpha(theme.palette.primary.light, 0.2)
            : error
              ? alpha(theme.palette.error.light, 0.1)
              : theme.palette.fileUpload.background.default,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: error
              ? alpha(theme.palette.error.light, 0.1)
              : theme.palette.fileUpload.background.hover,
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={accept}
          name={name}
          multiple={multiple}
          {...(webkitdirectory &&
            ({ webkitdirectory: 'true', directory: 'true' } as Record<string, string>))}
          onChange={handleInputChange}
        />

        <Stack
          gap={2}
          alignItems="center"
          sx={{
            pointerEvents: 'none',
            width: '100%',
            maxHeight: '100%',
            overflow: 'hidden',
          }}
        >
          {files.length > 0 ? (
            <Stack
              sx={{
                maxHeight: '240px',
                overflowY: 'auto',
                width: '100%',
                gap: 1,
                pointerEvents: 'auto',
              }}
            >
              {files.map((file, index) => (
                <Box
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: theme.spacing(1.5, 2),
                    backgroundColor: theme.palette.fileUpload.file.background,
                    borderRadius: theme.spacing(1),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      color={theme.palette.fileUpload.file.color.primary}
                      handleOverFlow
                    >
                      {file.name}
                    </Typography>
                    <Typography
                      variant="caption2"
                      color={theme.palette.fileUpload.file.color.secondary}
                    >
                      {formatSizeFromBytes(file.size)}
                    </Typography>
                  </Stack>
                  <IconButton
                    Icon={CloseIcon}
                    size={16}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(index);
                    }}
                  />
                </Box>
              ))}
            </Stack>
          ) : (
            <Stack alignItems="center" gap={2}>
              <UploadIcon />
              <Stack alignItems="center" gap={1}>
                <Typography variant="body1">
                  Drop {label || (multiple ? 'files' : 'file')} here
                </Typography>
                <Typography variant="body2" color="text.light">
                  or click to browse
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
      {error && (
        <Typography
          variant="body2"
          color="error"
          sx={{
            mt: 1,
            fontSize: '0.75rem',
          }}
        >
          {error}
        </Typography>
      )}
    </>
  );
};
